import {
    CATEGORY_BASEBALL,
    CATEGORY_BASKETBALL,
    CATEGORY_FOOTBALL,
    CATEGORY_MULTISPORT,
    CATEGORY_POKEMON,
} from '@fanatics/resn-api/instant-rips/constants/pack';

import { themeParams } from '~/components/gl/Foreground/data';

import { WaxChromeColors, WaxChromeSuperFractor } from './wax-chrome-pack-data';

export const ElasticEaseOut = 'elastic.out(1, 1)';
export const Power3EaseOut = 'power3.out';

export const MAX_WIDTH_DESKTOP = 2000;

export const VIDEO_SMALL = 768;
export const VIDEO_MEDIUM = 1280;
export const VIDEO_LARGE = 1600;

// 3d Layers
export const LAYER_SCENE = 0;
export const LAYER_PRE_SCENE = 1;
export const LAYER_FG = 3;
export const LAYER_UI = 4;
export const LAYER_BG = 5;
export const LAYER_CARD_INNER = 6;
export const LAYER_PACK_STENCIL = 7;
export const LAYER_PACK_SCENE = 8;

// Colors — keyed by stable id (order-independent). Wax parallels use the same ids as `wax-chrome-pack-data`.
const waxChromePalettes = Object.fromEntries(
    Object.values(WaxChromeColors).map((c) => [c.id, [c.color, c.color, c.color]])
);

export const PALETTE_THEME = {
    default: ['#000000', '#b676cf', '#0a44df'],
    base: ['#0b5b81', '#2d8b58', '#edd500'],
    chase: ['#c5b3d2', '#0a6ff7', '#042383'],
    grail: ['#e72e3c', '#cc8418', '#449a7a'],
    goldenTicket: ['#fff5d0', '#bc9b50', '#422b15'],
    mega: ['#ccc', '#ccc', '#ccc'],
    super: ['#93835E', '#93835E', '#93835E'],
    infinity: ['#8727BB', '#8727BB', '#8727BB'],
    ...waxChromePalettes,

    /** SuperFractor pack-open look: bright lemon/yellow glow, rich gold, deep orange-gold. */
    [WaxChromeSuperFractor.id]: WaxChromeSuperFractor.colors,
};

export const DEFAULT_PALETTE_ID = 'default';

export function getPaletteThemeFromId(paletteId) {
    const triplet = PALETTE_THEME[paletteId];
    return triplet ?? PALETTE_THEME[DEFAULT_PALETTE_ID];
}

/** Maps slab/raw card `data.tier` to a `PALETTE_THEME` key (same as `themeParams[].paletteId`). */
export function getPaletteIdForTier(tier) {
    const row = themeParams.find((t) => t.id === tier);
    return row?.paletteId ?? DEFAULT_PALETTE_ID;
}

// Scene reveals
export const BOX_DUR = 2;
export const PACK_DUR = 2;
export const CARDS_DUR = 2;
export const LIGHTS_DOWN_REVEAL_START = 4;
export const LIGHTS_DOWN_DUR = 9;
export const GRAIL_EFFECT_DUR = 6;
export const GRAIL_EFFECT_REVEAL_START = 3;
export const GRAIL_TUNNEL_SET = 1;
export const GRAIL_TUNNEL_REVEAL = 1.2;
export const GRAIL_TUNNEL_WIPE = 6;

export const COMPLETE_DUR = 1.5;

// Reveal animations
export const IN_DUR = 1;
export const PAUSE_DUR = 0.75;
export const OUT_DUR = 1;
export const TOTAL_DUR = IN_DUR + PAUSE_DUR + OUT_DUR;
export const OUT_START = IN_DUR + PAUSE_DUR;
export const IN_EASE = 'power3.out';
export const OUT_EASE = 'power3.in';

export const REVEAL = {
    [IN_DUR]: IN_DUR,
    [PAUSE_DUR]: PAUSE_DUR,
    [OUT_DUR]: OUT_DUR,
    [TOTAL_DUR]: TOTAL_DUR,
    [IN_EASE]: IN_EASE,
    [OUT_EASE]: OUT_EASE,
    [OUT_START]: OUT_START,
};

export const GRAIL_REVEALS = {
    CATEGORY: 'category',
    LIGHTS_DOWN: 'lights-down',
    TUNNEL: 'grail-tunnel',
};

// Cards Mode
export const CARDS_MODE = {
    DEFAULT: 'default',
    FAN: 'fan',
};

export const PackBaseTier = {
    name: 'Base',
    id: 'base',
    color: '#ccc',
};

// Pack Constants
export const PackSuperTier = {
    name: 'Super',
    id: 'super',
    color: '#763ee8', // #452ebf
    image: '/textures/pack/test/super@lg.webp',
    paletteId: 'base',
};

export const PackMegaTier = {
    name: 'Mega',
    id: 'mega',
    color: '#ccc', // #afafaf
    image: '/textures/pack/test/mega@lg.webp',
    paletteId: 'mega',
};

export const PackUltraTier = {
    name: 'Ultra',
    id: 'ultra',
    color: '#c8b58c', // #a28b61
    image: '/textures/pack/test/ultra@lg.webp',
    paletteId: 'super',
};

export const PackInfinityTier = {
    name: 'Infinity',
    id: 'infinity',
    color: '#5d5d69', // #44424f #16152c
    image: '/textures/pack/test/infinity@lg.webp',
    paletteId: 'infinity',
};

export const PackTiers = {
    [1]: PackSuperTier,
    [2]: PackMegaTier,
    [3]: PackUltraTier,
    [4]: PackInfinityTier,
};

export const PackTiersArray = Object.values(PackTiers);

export const PackCategories = Object.fromEntries(
    [
        CATEGORY_BASEBALL,
        CATEGORY_BASKETBALL,
        CATEGORY_FOOTBALL,
        CATEGORY_MULTISPORT,
        CATEGORY_POKEMON,
    ].map((value) => [value, value])
);

export const PackCategoriesArray = Object.values(PackCategories);

// Pack Types
export const PACK_TYPES = {
    REPACK: 'repack',
    WAX: 'wax',
};

export const PACK_TYPES_ARRAY = Object.values(PACK_TYPES);

// Camera constants
export const CAMERA_PACK_DISTANCE = 2;
export const CAMERA_BASE_DISTANCE = 1.8;
export const CAMERA_FOV = 50;

// Raw card geometry
export const RAW_CARD_ASPECT = 728 / 1024;
export const RAW_CARD_THICKNESS = 0.008;
export const RAW_CARD_SCALE = 0.84;
